import './N8nToolsConfig.css';
import { useState, useContext, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../authContext';

const FIELD_TYPES = ['text', 'number', 'textarea', 'select'];
const isValidWebhookUrl = (url) => /^https?:\/\//i.test((url || '').trim());
const emptyField = () => ({
    _key: Date.now() + Math.random(),
    name: '',
    label: '',
    type: 'text',
    required: false,
    placeholder: '',
    options: ''
});

const emptyTool = () => ({
    id: crypto.randomUUID ? crypto.randomUUID() : `tool-${Date.now()}`,
    name: '',
    description: '',
    webhook_url: '',
    name_header: '',
    auth_enabled: true,
    output_config: {
        mode: 'raw_json',
        llm_output_instruction: ''
    },
    secret_masked: '',
    input_schema: { fields: [] },
    active: true,
    requires_confirmation: false,
    timeout_ms: 30000,
    created_at: new Date().toISOString().split('T')[0],
    updated_at: new Date().toISOString().split('T')[0]
});

function SecretRevealBanner({ secret, onDismiss, tConfig }) {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(secret).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };
    return (
        <div className="n8n-secret-banner" role="alert">
            <p className="n8n-secret-banner-warning"><strong>{tConfig('n8n_secret_reveal_warning')}</strong></p>
            <div className="n8n-secret-banner-row">
                <code className="n8n-secret-value">{secret}</code>
                <button type="button" className="n8n-btn n8n-btn-copy" onClick={handleCopy}>
                    {copied ? tConfig('n8n_secret_copied') : tConfig('n8n_secret_copy')}
                </button>
            </div>
            <button type="button" className="n8n-btn n8n-btn-dismiss" onClick={onDismiss}>✕</button>
        </div>
    );
}

function InstructionsPanel({ tConfig }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="n8n-instructions">
            <button type="button" className="n8n-instructions-toggle" onClick={() => setOpen(v => !v)} aria-expanded={open}>
                <img src="/imagens/alert-circle.svg" alt="" className="n8n-instructions-icon" />
                {tConfig('n8n_instructions_title')}
                <span className="n8n-instructions-chevron">{open ? '▲' : '▼'}</span>
            </button>
            {open && (
                <ol className="n8n-instructions-list">
                    <li>{tConfig('n8n_instructions_step1')}</li>
                    <li>{tConfig('n8n_instructions_step2')}</li>
                    <li>{tConfig('n8n_instructions_step3')}</li>
                    <li>{tConfig('n8n_instructions_step4')}</li>
                </ol>
            )}
        </div>
    );
}

function SchemaFieldEditor({ field, onChange, onRemove, tConfig }) {
    return (
        <div className="n8n-schema-field">
            <div className="n8n-schema-field-row">
                <input
                    className="n8n-input n8n-input-sm"
                    placeholder={tConfig('n8n_schema_field_name')}
                    value={field.name}
                    onChange={e => onChange({ ...field, name: e.target.value.replace(/\s/g, '_').toLowerCase() })}
                />
                <input
                    className="n8n-input n8n-input-sm"
                    placeholder={tConfig('n8n_schema_field_label')}
                    value={field.label}
                    onChange={e => onChange({ ...field, label: e.target.value })}
                />
                <select
                    className="n8n-select n8n-input-sm"
                    value={field.type}
                    onChange={e => onChange({ ...field, type: e.target.value })}
                >
                    {FIELD_TYPES.map(ft => (
                        <option key={ft} value={ft}>{tConfig(`n8n_schema_field_type_${ft}`)}</option>
                    ))}
                </select>
                <label className="n8n-checkbox-label">
                    <input
                        type="checkbox"
                        checked={field.required}
                        onChange={e => onChange({ ...field, required: e.target.checked })}
                    />
                    {tConfig('n8n_schema_field_required')}
                </label>
                <button type="button" className="n8n-btn n8n-btn-danger-sm" onClick={onRemove} title={tConfig('n8n_schema_remove_field')}>
                    ✕
                </button>
            </div>
            <input
                className="n8n-input n8n-input-full"
                placeholder={tConfig('n8n_schema_field_placeholder')}
                value={field.placeholder || ''}
                onChange={e => onChange({ ...field, placeholder: e.target.value })}
            />
            {field.type === 'select' && (
                <input
                    className="n8n-input n8n-input-full"
                    placeholder={tConfig('n8n_schema_field_options')}
                    value={field.options || ''}
                    onChange={e => onChange({ ...field, options: e.target.value })}
                />
            )}
        </div>
    );
}

function ToolEditor({ tool, allTools, assistantId, onSave, onCancel, tConfig, accessToken }) {
    const [form, setForm] = useState(() => ({
        ...tool,
        output_config: {
            mode: tool?.output_config?.mode || 'raw_json',
            llm_output_instruction: tool?.output_config?.llm_output_instruction || ''
        },
        input_schema: {
            fields: (tool.input_schema?.fields || []).map(f => ({
                ...f,
                _key: Math.random(),
                options: Array.isArray(f.options) ? f.options.join(', ') : (f.options || '')
            }))
        }
    }));
    const [revealedSecret, setRevealedSecret] = useState(null);
    const [generatingSecret, setGeneratingSecret] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [errors, setErrors] = useState({});
    const isPersistedTool = allTools.some(existing => existing.id === form.id);

    const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

    const validate = () => {
        const errs = {};
        if (!form.name.trim()) errs.name = tConfig('n8n_tool_name_required');
        if (!isValidWebhookUrl(form.webhook_url)) errs.webhook_url = tConfig('n8n_tool_url_required');
        const duplicate = allTools.some(t => t.name.trim().toLowerCase() === form.name.trim().toLowerCase() && t.id !== form.id);
        if (duplicate) errs.name = tConfig('n8n_tool_name_duplicate');
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSave = () => {
        if (!validate()) return;
        onSave({
            ...form,
            name: form.name.trim(),
            description: form.description.trim(),
            webhook_url: form.webhook_url.trim(),
            name_header: form.name_header.trim(),
            auth_enabled: form.auth_enabled !== false,
            output_config: {
                mode: ['raw_json', 'llm_text', 'both'].includes(form?.output_config?.mode)
                    ? form.output_config.mode
                    : 'raw_json',
                llm_output_instruction: String(form?.output_config?.llm_output_instruction || '').trim().slice(0, 4000)
            },
            requires_confirmation: form.requires_confirmation === true,
            timeout_ms: Math.min(60000, Math.max(5000, Number(form.timeout_ms) || 30000)),
            input_schema: {
                fields: (form.input_schema?.fields || [])
                    .filter(f => f.name && f.name.trim())
                    .map(({ _key, ...rest }) => ({
                        ...rest,
                        name: rest.name.trim(),
                        label: (rest.label || '').trim(),
                        placeholder: (rest.placeholder || '').trim(),
                        options: rest.type === 'select'
                            ? String(rest.options || '').split(',').map(v => v.trim()).filter(Boolean)
                            : []
                    }))
            },
            updated_at: new Date().toISOString().split('T')[0]
        });
    };

    const addField = () => setForm(prev => ({
        ...prev,
        input_schema: { fields: [...(prev.input_schema?.fields || []), emptyField()] }
    }));
    const updateField = (idx, updated) => setForm(prev => {
        const fields = [...(prev.input_schema?.fields || [])];
        fields[idx] = updated;
        return { ...prev, input_schema: { fields } };
    });
    const removeField = (idx) => setForm(prev => ({
        ...prev,
        input_schema: { fields: (prev.input_schema?.fields || []).filter((_, i) => i !== idx) }
    }));

    const handleGenerateSecret = async () => {
        if (!assistantId || !isPersistedTool || form.auth_enabled === false) return;
        setGeneratingSecret(true);
        try {
            const resp = await fetch(`${process.env.REACT_APP_API_URL}/post_n8n_generate_secret`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
                body: JSON.stringify({ assistant_id: assistantId, tool_id: form.id })
            });
            const data = await resp.json();
            if (data.status) {
                setRevealedSecret(data.secret);
                setForm(prev => ({ ...prev, secret_masked: data.secret_masked }));
            }
        } finally {
            setGeneratingSecret(false);
        }
    };

    const handleTest = async () => {
        if (!assistantId || !isPersistedTool) {
            setTestResult({ ok: false, msg: 'Guarde a ferramenta antes de testar o webhook.' });
            return;
        }
        if (!isValidWebhookUrl(form.webhook_url)) {
            setTestResult({ ok: false, msg: tConfig('n8n_tool_url_required') });
            return;
        }
        const authorization= form.name_header ? form.name_header: 'Authorization';
        setTesting(true);
        setTestResult(null);
        try {
            const resp = await fetch(`${process.env.REACT_APP_API_URL}/post_n8n_tool_test`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
                body: JSON.stringify({ assistant_id: assistantId, tool_id: form.id, authorization })
            });
            const data = await resp.json();
            if (data.error === 'N8N_TIMEOUT') setTestResult({ ok: false, msg: tConfig('n8n_tool_test_timeout') });
            else if (!data.status || !data.reachable) setTestResult({ ok: false, msg: tConfig('n8n_tool_test_fail') });
            else setTestResult({ ok: true, msg: tConfig('n8n_tool_test_ok').replace('{{status}}', data.http_status) });
        } finally {
            setTesting(false);
        }
    };

    return (
        <div className="n8n-tool-editor">
            {revealedSecret && <SecretRevealBanner secret={revealedSecret} onDismiss={() => setRevealedSecret(null)} tConfig={tConfig} />}
            <div className="n8n-field-group">
                <label className="n8n-label">{tConfig('n8n_tool_name')} *</label>
                <input className={`n8n-input ${errors.name ? 'n8n-input-error' : ''}`} placeholder={tConfig('n8n_tool_name_placeholder')} value={form.name} onChange={e => setField('name', e.target.value)} />
                {errors.name && <span className="n8n-error-text">{errors.name}</span>}
            </div>
            <div className="n8n-field-group">
                <label className="n8n-label">{tConfig('n8n_tool_description')}</label>
                <input className="n8n-input" placeholder={tConfig('n8n_tool_description_placeholder')} value={form.description} onChange={e => setField('description', e.target.value)} />
            </div>
            <div className="n8n-field-group">
                <label className="n8n-label">{tConfig('n8n_webhook_url')} *</label>
                <div className="n8n-input-row">
                    <input className={`n8n-input ${errors.webhook_url ? 'n8n-input-error' : ''}`} placeholder={tConfig('n8n_webhook_url_placeholder')} value={form.webhook_url} onChange={e => setField('webhook_url', e.target.value)} />
                    <button type="button" className="n8n-btn n8n-btn-secondary" onClick={handleTest} disabled={testing || !assistantId || !isPersistedTool}>
                        {testing ? tConfig('n8n_testing') : tConfig('n8n_tool_test')}
                    </button>
                </div>
                <label className="n8n-label">{tConfig('name_header_auth')} </label>
                <div className="n8n-input-row">
                    <input className={`n8n-input`} placeholder={tConfig('name_header_auth')} value={form.name_header} onChange={e => setField('name_header', e.target.value)} />
                    
                </div>
                {errors.webhook_url && <span className="n8n-error-text">{errors.webhook_url}</span>}
                {testResult && <span className={`n8n-test-result ${testResult.ok ? 'ok' : 'fail'}`}>{testResult.ok ? '✓' : '✗'} {testResult.msg}</span>}
            </div>
            <div className="n8n-field-group">
                <label className="n8n-label">{tConfig('n8n_secret_section')}</label>
                <div className="n8n-secret-row">
                    <span className="n8n-secret-masked">
                        {form.auth_enabled === false
                            ? 'Autenticação desativada para este webhook.'
                            : (form.secret_masked ? `${tConfig('n8n_secret_masked_label')}: ${form.secret_masked}` : tConfig('n8n_secret_no_secret'))}
                    </span>
                    <button
                        type="button"
                        className="n8n-btn n8n-btn-secondary"
                        onClick={handleGenerateSecret}
                        disabled={form.auth_enabled === false || generatingSecret || !assistantId || !isPersistedTool}
                    >
                        {generatingSecret ? tConfig('n8n_generating_secret') : form.secret_masked ? tConfig('n8n_secret_rotate') : tConfig('n8n_secret_generate')}
                    </button>
                </div>
            </div>
            <InstructionsPanel tConfig={tConfig} />
            <div className="n8n-field-group">
                <label className="n8n-label">Output no chat</label>
                <div className="n8n-field-group n8n-row">
                    <div className="n8n-field-group" style={{ flex: 1 }}>
                        <label className="n8n-label">Formato</label>
                        <select
                            className="n8n-select n8n-input-sm"
                            value={form?.output_config?.mode || 'raw_json'}
                            onChange={e => setForm(prev => ({
                                ...prev,
                                output_config: { ...(prev.output_config || {}), mode: e.target.value }
                            }))}
                        >
                            <option value="raw_json">JSON raw</option>
                            <option value="llm_text">Texto transformado (LLM)</option>
                            <option value="both">Texto + JSON raw</option>
                        </select>
                    </div>
                </div>
                {['llm_text', 'both'].includes(form?.output_config?.mode || '') && (
                    <div className="n8n-field-group">
                        <label className="n8n-label">{tConfig('n8n_output_llm_instruction_label')}</label>
                        <p className="n8n-output-instruction-hint">{tConfig('n8n_output_llm_instruction_hint')}</p>
                        <textarea
                            className="n8n-input n8n-textarea"
                            rows={4}
                            placeholder={tConfig('n8n_output_llm_instruction_placeholder')}
                            value={form?.output_config?.llm_output_instruction || ''}
                            onChange={e => setForm(prev => ({
                                ...prev,
                                output_config: {
                                    ...(prev.output_config || {}),
                                    llm_output_instruction: e.target.value.slice(0, 4000)
                                }
                            }))}
                        />
                    </div>
                )}
            </div>
            <div className="n8n-field-group">
                <label className="n8n-label">{tConfig('n8n_schema_section')}</label>
                <div className="n8n-schema-fields">
                    {(form.input_schema?.fields || []).map((field, idx) => (
                        <SchemaFieldEditor
                            key={field._key || idx}
                            field={field}
                            onChange={updated => updateField(idx, updated)}
                            onRemove={() => removeField(idx)}
                            tConfig={tConfig}
                        />
                    ))}
                </div>
                <button type="button" className="n8n-btn n8n-btn-ghost" onClick={addField}>
                    + {tConfig('n8n_schema_add_field')}
                </button>
            </div>
            <div className="n8n-field-group n8n-row">
                <div className="n8n-field-group" style={{ flex: 1 }}>
                    <label className="n8n-label">{tConfig('n8n_timeout')}</label>
                    <input className="n8n-input n8n-input-sm" type="number" min={5000} max={60000} step={1000} value={form.timeout_ms} onChange={e => setField('timeout_ms', e.target.value)} />
                </div>
                <label className="n8n-checkbox-label n8n-active-toggle">
                    <input type="checkbox" checked={form.auth_enabled !== false} onChange={e => setField('auth_enabled', e.target.checked)} />
                    Requer autenticação
                </label>
                <label className="n8n-checkbox-label n8n-active-toggle">
                    <input type="checkbox" checked={form.active} onChange={e => setField('active', e.target.checked)} />
                    {tConfig('n8n_tool_active')}
                </label>
                <label className="n8n-checkbox-label n8n-active-toggle">
                    <input type="checkbox" checked={form.requires_confirmation === true} onChange={e => setField('requires_confirmation', e.target.checked)} />
                    Requer confirmação
                </label>
            </div>
            <div className="n8n-editor-actions">
                <button type="button" className="n8n-btn n8n-btn-ghost" onClick={onCancel}>✕</button>
                <button type="button" className="n8n-btn n8n-btn-primary" onClick={handleSave}>{tConfig('n8n_tool_save')}</button>
            </div>
        </div>
    );
}

function ToolCard({ tool, onEdit, onRemove, tConfig }) {
    return (
        <div className={`n8n-tool-card ${!tool.active ? 'n8n-tool-card--inactive' : ''}`}>
            <div className="n8n-tool-card-header">
                <div className="n8n-tool-card-info"><span className="n8n-tool-card-name">{tool.name}</span>{!tool.active && <span className="n8n-tool-card-badge">inativo</span>}</div>
                <div className="n8n-tool-card-actions">
                    <button type="button" className="n8n-btn n8n-btn-ghost-sm" onClick={() => onEdit(tool)}>{tConfig('n8n_tool_edit')}</button>
                    <button type="button" className="n8n-btn n8n-btn-danger-sm" onClick={() => onRemove(tool)}>{tConfig('n8n_tool_remove')}</button>
                </div>
            </div>
            {tool.description && <p className="n8n-tool-card-description">{tool.description}</p>}
            <p className="n8n-tool-card-url" title={tool.webhook_url}>{tool.webhook_url || '—'}</p>
            <p className="n8n-tool-card-fields">
                {(tool.input_schema?.fields || []).length} campo(s) configurado(s)
            </p>
        </div>
    );
}

export default function N8nToolsConfig({ assistant, assistantId, onUpdate, accessToken: accessTokenProp }) {
    const { t: tConfig } = useTranslation('assistantConfiguration');
    const { accessToken: ctxToken } = useContext(AuthContext);
    const token = accessTokenProp || ctxToken;
    const tools = assistant?.n8n_tools || [];
    const [editingTool, setEditingTool] = useState(null);
    const [isNew, setIsNew] = useState(false);
    const [confirmRemove, setConfirmRemove] = useState(null);

    const handleAddTool = useCallback(() => { setEditingTool(emptyTool()); setIsNew(true); }, []);
    const handleEditTool = useCallback((tool) => { setEditingTool({ ...tool }); setIsNew(false); }, []);
    const handleSaveTool = useCallback((saved) => {
        const updated = isNew ? [...tools, saved] : tools.map(t => t.id === saved.id ? saved : t);
        onUpdate('n8n_tools', updated);
        setEditingTool(null);
        setIsNew(false);
    }, [tools, isNew, onUpdate]);
    const handleRemoveTool = useCallback((tool) => setConfirmRemove(tool), []);
    const handleConfirmRemove = useCallback(() => {
        if (!confirmRemove) return;
        onUpdate('n8n_tools', tools.filter(t => t.id !== confirmRemove.id));
        setConfirmRemove(null);
    }, [tools, confirmRemove, onUpdate]);

    return (
        <div className="n8n-tools-config">
            {tools.length === 0 && !editingTool && <p className="n8n-no-tools">{tConfig('n8n_no_tools')}</p>}
            {!editingTool && tools.map(tool => <ToolCard key={tool.id} tool={tool} onEdit={handleEditTool} onRemove={handleRemoveTool} tConfig={tConfig} />)}
            {editingTool && <ToolEditor tool={editingTool} allTools={tools} assistantId={assistantId} onSave={handleSaveTool} onCancel={() => { setEditingTool(null); setIsNew(false); }} tConfig={tConfig} accessToken={token} />}
            {!editingTool && <button type="button" className="n8n-btn n8n-btn-add" onClick={handleAddTool}>+ {tConfig('n8n_add_tool')}</button>}
            {confirmRemove && (
                <div className="n8n-confirm-overlay" role="dialog">
                    <div className="n8n-confirm-modal">
                        <p>{tConfig('n8n_tool_remove_confirm')}</p>
                        <p className="n8n-confirm-name">«{confirmRemove.name}»</p>
                        <div className="n8n-confirm-actions">
                            <button type="button" className="n8n-btn n8n-btn-ghost" onClick={() => setConfirmRemove(null)}>Cancelar</button>
                            <button type="button" className="n8n-btn n8n-btn-danger" onClick={handleConfirmRemove}>Remover</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
